import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  ClipboardDocumentCheckIcon,
  DocumentMagnifyingGlassIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import QuickScoreReport from '../features/assessment/QuickScoreReport';
import {
  assessmentSections,
  calculateQuickScore,
  quickScoreQuestions,
  QuickScoreResult,
  sampleQuickScoreAnswers
} from '../features/assessment/assessmentEngine';
import { trackEvent } from '../utils/analytics';

type AssessmentScreen = 'intro' | 'questions' | 'report';

const Shell = styled.section`
  width: 100%;
  color: var(--davinci-grey);
`;

const Intro = styled.div`
  width: min(920px, 100%);
  margin: 0 auto;
  padding: clamp(24px, 5vw, 56px);
  border: 1px solid rgba(11, 152, 206, 0.14);
  border-radius: 18px;
  background: white;
  box-shadow: 0 22px 60px rgba(20, 53, 98, 0.14);
`;

const Eyebrow = styled.p`
  margin: 0 0 12px;
  color: var(--davinci-denim);
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const IntroTitle = styled.h2`
  max-width: 760px;
  margin: 0 0 18px;
  color: var(--text-primary);
  font-size: clamp(2rem, 5vw, 3.6rem);
  line-height: 1.08;
  letter-spacing: -0.04em;
`;

const IntroCopy = styled.p`
  max-width: 720px;
  margin: 0 0 30px;
  color: var(--davinci-grey);
  font-size: 1rem;
  line-height: 1.68;
`;

const PromiseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin: 30px 0;
  border: 1px solid var(--davinci-cloud);
  border-radius: 14px;
  overflow: hidden;

  @media (max-width: 680px) { grid-template-columns: 1fr; }
`;

const PromiseItem = styled.div`
  min-height: 145px;
  padding: 22px;
  border-right: 1px solid var(--davinci-cloud);

  &:last-child { border-right: 0; }
  svg { width: 24px; color: var(--davinci-blue); }
  strong { display: block; margin: 14px 0 7px; color: var(--davinci-midnight); }
  span { display: block; color: var(--davinci-grey); font-size: 0.86rem; line-height: 1.5; }

  @media (max-width: 680px) {
    min-height: auto;
    border-right: 0;
    border-bottom: 1px solid var(--davinci-cloud);
    &:last-child { border-bottom: 0; }
  }
`;

const WorkflowForm = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 0.8fr;
  gap: 18px;
  padding: 26px;
  border-radius: 14px;
  background: var(--color-secondary-lightest);

  @media (max-width: 680px) { grid-template-columns: 1fr; padding: 20px; }
`;

const Field = styled.div`
  label {
    display: block;
    margin: 0 0 8px;
    color: var(--davinci-midnight);
    font-size: 0.82rem;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: none;
  }

  input, select {
    width: 100%;
    min-height: 50px;
    padding: 11px 13px;
    border: 1px solid var(--davinci-stone);
    border-radius: 8px;
    background: white;
    color: var(--davinci-grey);
    font: inherit;
  }

  input:focus, select:focus {
    outline: 3px solid rgba(11, 152, 206, 0.28);
    outline-offset: 1px;
    border-color: var(--davinci-denim);
  }

  small {
    display: block;
    margin-top: 7px;
    color: var(--davinci-stone);
    font-size: 0.76rem;
    line-height: 1.4;
  }
`;

const FormError = styled.p`
  grid-column: 1 / -1;
  margin: -4px 0 0;
  color: var(--davinci-midnight);
  font-size: 0.84rem;
  font-weight: 700;
`;

const IntroActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-top: 22px;

  @media (max-width: 560px) { align-items: stretch; flex-direction: column-reverse; }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 50px;
  padding: 13px 24px;
  border: 1px solid var(--davinci-blue);
  border-radius: 8px;
  background: var(--davinci-blue-fill);
  color: white;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  transition: transform var(--transition-base), box-shadow var(--transition-base), background-color var(--transition-base);

  svg { width: 19px; }
  &:hover { transform: translateY(-2px); border-color: var(--davinci-denim); background: var(--davinci-denim); box-shadow: var(--shadow-md); }
  &:active { transform: translateY(0); }
  &:focus-visible { outline: 3px solid rgba(11, 152, 206, 0.34); outline-offset: 2px; }
  &:disabled { cursor: not-allowed; opacity: 0.55; }
`;

const TextButton = styled.button`
  min-height: 44px;
  padding: 8px 0;
  border: 0;
  background: transparent;
  color: var(--davinci-denim);
  font: inherit;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 4px;

  &:focus-visible { outline: 3px solid rgba(11, 152, 206, 0.3); outline-offset: 3px; }
`;

const AssessmentLayout = styled.div`
  display: grid;
  grid-template-columns: 230px minmax(0, 1fr);
  gap: clamp(24px, 5vw, 56px);
  width: min(1040px, 100%);
  margin: 0 auto;
  padding: clamp(16px, 4vw, 42px) 0;

  @media (max-width: 780px) { grid-template-columns: 1fr; }
`;

const SectionRail = styled.aside`
  align-self: start;
  position: sticky;
  top: 108px;

  @media (max-width: 780px) { position: static; }
`;

const RailTitle = styled.p`
  margin: 0 0 14px;
  color: var(--davinci-midnight);
  font-size: 0.8rem;
  font-weight: 800;
`;

const RailList = styled.ol`
  display: grid;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;

  @media (max-width: 780px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const RailItem = styled.li<{ $active: boolean; $complete: boolean }>`
  display: grid;
  grid-template-columns: 30px 1fr;
  gap: 10px;
  align-items: center;
  min-height: 52px;
  margin: 0;
  padding: 8px 0;
  border-left: 2px solid ${props => props.$active ? 'var(--davinci-blue)' : 'var(--davinci-cloud)'};
  color: ${props => props.$active ? 'var(--davinci-midnight)' : 'var(--davinci-stone)'};

  span:first-child {
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    margin-left: -13px;
    border: 2px solid ${props => props.$active || props.$complete ? 'var(--davinci-blue)' : 'var(--davinci-cloud)'};
    border-radius: 50%;
    background: ${props => props.$complete ? 'var(--davinci-blue)' : 'white'};
    color: ${props => props.$complete ? 'white' : 'inherit'};
    font-size: 0.69rem;
    font-weight: 800;
  }

  span:last-child { font-size: 0.8rem; font-weight: ${props => props.$active ? 800 : 600}; }

  svg { width: 13px; }

  @media (max-width: 780px) {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-height: auto;
    padding: 0 5px 10px;
    border-left: 0;
    border-bottom: 2px solid ${props => props.$active ? 'var(--davinci-blue)' : 'var(--davinci-cloud)'};
    text-align: center;

    span:first-child { margin: 0 0 -23px; transform: translateY(23px); }
    span:last-child { display: none; }
  }
`;

const QuestionPanel = styled.section`
  min-width: 0;
  padding: clamp(24px, 5vw, 48px);
  border: 1px solid rgba(11, 152, 206, 0.14);
  border-radius: 18px;
  background: white;
  box-shadow: 0 18px 50px rgba(20, 53, 98, 0.12);
`;

const ProgressMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  color: var(--davinci-stone);
  font-size: 0.76rem;
  font-weight: 700;
`;

const ProgressTrack = styled.div<{ $progress: number }>`
  height: 5px;
  margin-bottom: 36px;
  overflow: hidden;
  background: var(--davinci-steam, #f0f0f0);

  &::after {
    content: '';
    display: block;
    width: ${props => props.$progress}%;
    height: 100%;
    background: var(--davinci-blue);
    transition: width 180ms ease-out;
  }

  @media (prefers-reduced-motion: reduce) { &::after { transition: none; } }
`;

const QuestionSection = styled.p`
  margin: 0 0 10px;
  color: var(--davinci-denim);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const QuestionTitle = styled.h2`
  margin: 0 0 12px;
  color: var(--text-primary);
  font-size: clamp(1.55rem, 4vw, 2.45rem);
  line-height: 1.2;
  letter-spacing: -0.03em;

  &:focus { outline: none; }
`;

const QuestionGuidance = styled.p`
  margin: 0 0 30px;
  color: var(--davinci-grey);
  font-size: 0.92rem;
  line-height: 1.56;
`;

const Options = styled.fieldset`
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 0;
  border: 0;

  legend {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

const OptionLabel = styled.label<{ $selected: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: 26px 1fr;
  gap: 14px;
  align-items: start;
  min-height: 78px;
  margin: 0;
  padding: 18px;
  border: 1px solid ${props => props.$selected ? 'var(--davinci-denim)' : 'var(--davinci-cloud)'};
  border-left: 4px solid ${props => props.$selected ? 'var(--davinci-blue)' : 'transparent'};
  border-radius: 10px;
  background: ${props => props.$selected ? 'rgba(143, 215, 229, 0.12)' : 'white'};
  cursor: pointer;
  letter-spacing: 0;
  text-transform: none;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast), transform var(--transition-fast);

  &:hover { transform: translateY(-1px); border-color: var(--davinci-blue); box-shadow: var(--shadow-sm); }
  &:focus-within { outline: 3px solid rgba(11, 152, 206, 0.26); outline-offset: 2px; }

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
  }
`;

const OptionMark = styled.span<{ $selected: boolean }>`
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border: 2px solid ${props => props.$selected ? 'var(--davinci-denim)' : 'var(--davinci-stone)'};
  border-radius: 50%;
  background: ${props => props.$selected ? 'var(--davinci-denim)' : 'white'};
  color: white;

  svg { width: 14px; opacity: ${props => props.$selected ? 1 : 0}; }
`;

const OptionCopy = styled.span`
  strong {
    display: block;
    margin-bottom: 4px;
    color: var(--davinci-midnight);
    font-size: 0.95rem;
  }

  small {
    display: block;
    color: var(--davinci-grey);
    font-size: 0.8rem;
    font-weight: 500;
    line-height: 1.45;
  }
`;

const Navigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 32px;
  padding-top: 20px;
  border-top: 1px solid var(--davinci-cloud);

  @media (max-width: 480px) { align-items: stretch; flex-direction: column-reverse; }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 46px;
  padding: 10px 14px;
  border: 1px solid var(--davinci-cloud);
  border-radius: 8px;
  background: white;
  color: var(--davinci-midnight);
  font: inherit;
  font-weight: 700;
  cursor: pointer;

  svg { width: 18px; }
  &:hover { border-color: var(--davinci-blue); }
  &:focus-visible { outline: 3px solid rgba(11, 152, 206, 0.28); outline-offset: 2px; }
`;

const AIReadinessAssessment: React.FC<{ onClose?: () => void }> = () => {
  const [screen, setScreen] = useState<AssessmentScreen>('intro');
  const [workflowName, setWorkflowName] = useState('');
  const [respondentRole, setRespondentRole] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [result, setResult] = useState<QuickScoreResult | null>(null);
  const [formError, setFormError] = useState('');
  const [isSample, setIsSample] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const questionHeadingRef = useRef<HTMLHeadingElement>(null);

  const question = quickScoreQuestions[currentQuestion];
  const currentSection = useMemo(
    () => assessmentSections.find((section) => section.number === question?.section),
    [question]
  );
  const progress = Math.round(((currentQuestion + 1) / quickScoreQuestions.length) * 100);

  useEffect(() => {
    if (screen === 'questions') questionHeadingRef.current?.focus();
  }, [currentQuestion, screen]);

  useEffect(() => {
    try {
      const raw = JSON.parse(sessionStorage.getItem('davinci-quick-score-v1') || 'null');
      if (raw && typeof raw.workflowName === 'string' && raw.workflowName.trim()) {
        const restored: Record<string, string> = {};
        quickScoreQuestions.forEach(item => {
          if (item.options.some(option => option.value === raw.answers?.[item.id])) restored[item.id] = raw.answers[item.id];
        });
        setWorkflowName(raw.workflowName.slice(0, 200));
        setRespondentRole(typeof raw.respondentRole === 'string' ? raw.respondentRole.slice(0, 200) : '');
        setAnswers(restored);
        const firstMissing = quickScoreQuestions.findIndex(item => !restored[item.id]);
        if (raw.screen === 'report' && firstMissing === -1) {
          setResult(calculateQuickScore(restored)); setScreen('report');
        } else if (raw.screen === 'questions') {
          setCurrentQuestion(firstMissing === -1 ? quickScoreQuestions.length - 1 : firstMissing);
          setScreen('questions');
        }
      }
    } catch { /* Storage may be unavailable; the free assessment still works. */ }
    setDraftLoaded(true);
  }, []);

  useEffect(() => {
    if (!draftLoaded || isSample) return;
    try {
      sessionStorage.setItem('davinci-quick-score-v1', JSON.stringify({ workflowName, respondentRole, answers, screen }));
    } catch { /* Printing and completing the assessment do not depend on browser storage. */ }
  }, [draftLoaded, isSample, workflowName, respondentRole, answers, screen]);

  const startAssessment = () => {
    if (!workflowName.trim()) {
      setFormError('Name one workflow so the report can be specific.');
      return;
    }

    setFormError('');
    setIsSample(false);
    setScreen('questions');
    trackEvent('assessment_start', { tier: 'quick_score' });
  };

  const showSampleReport = () => {
    const sampleResult = calculateQuickScore(sampleQuickScoreAnswers);
    setWorkflowName('Service request intake');
    setRespondentRole('Operations leader');
    setAnswers(sampleQuickScoreAnswers);
    setResult(sampleResult);
    setIsSample(true);
    setScreen('report');
    trackEvent('assessment_sample_report_view');
  };

  const advance = () => {
    if (!question || !answers[question.id]) return;

    if (currentQuestion === quickScoreQuestions.length - 1) {
      const completedResult = calculateQuickScore(answers);
      setResult(completedResult);
      setScreen('report');
      trackEvent('assessment_complete', {
        tier: 'quick_score',
        grade: completedResult.grade,
        route: completedResult.route
      });
      return;
    }

    setCurrentQuestion((index) => index + 1);
  };

  const goBack = () => {
    if (currentQuestion === 0) {
      setScreen('intro');
      return;
    }
    setCurrentQuestion((index) => index - 1);
  };

  const reset = () => {
    setScreen('intro');
    setAnswers({});
    setCurrentQuestion(0);
    setResult(null);
    setIsSample(false);
    if (isSample) {
      setWorkflowName('');
      setRespondentRole('');
    }
  };

  const printReport = () => {
    trackEvent('assessment_report_print', { tier: 'quick_score', sample: isSample });
    window.print();
  };

  if (screen === 'report' && result) {
    return (
      <QuickScoreReport
        workflowName={workflowName}
        respondentRole={respondentRole}
        result={result}
        isSample={isSample}
        onPrint={printReport}
        onRetake={reset}
      />
    );
  }

  if (screen === 'intro') {
    return (
      <Shell>
        <Intro>
          <Eyebrow>Free · 12 questions · 7–10 minutes</Eyebrow>
          <IntroTitle>Start with one workflow, not an organization-wide guess.</IntroTitle>
          <IntroCopy>
            You will receive a grade, two separate scores, a recommended solution route, and one practical next step. The result appears before we ask for contact information.
          </IntroCopy>

          <PromiseGrid>
            <PromiseItem>
              <DocumentMagnifyingGlassIcon aria-hidden="true" />
              <strong>Route the work</strong>
              <span>AI-assisted, hybrid, deterministic automation, or human-led.</span>
            </PromiseItem>
            <PromiseItem>
              <UserIcon aria-hidden="true" />
              <strong>Protect employee authority</strong>
              <span>Review, correction, and exception handling stay visible.</span>
            </PromiseItem>
            <PromiseItem>
              <ClipboardDocumentCheckIcon aria-hidden="true" />
              <strong>Leave with a report</strong>
              <span>Print or save a three-page decision snapshot without a gate.</span>
            </PromiseItem>
          </PromiseGrid>

          <WorkflowForm data-clarity-mask="True">
            <Field>
              <label htmlFor="workflow-name">Workflow to assess</label>
              <input
                id="workflow-name"
                value={workflowName}
                onChange={(event) => setWorkflowName(event.target.value)}
                placeholder="Example: service request intake"
                maxLength={100}
              />
              <small>Use a specific process employees perform repeatedly.</small>
            </Field>
            <Field>
              <label htmlFor="respondent-role">Your role (optional)</label>
              <select id="respondent-role" value={respondentRole} onChange={(event) => setRespondentRole(event.target.value)}>
                <option value="">Select a role</option>
                <option>Executive or director</option>
                <option>Operations leader</option>
                <option>IT leader or technician</option>
                <option>Workflow operator</option>
                <option>Advisor or consultant</option>
              </select>
            </Field>
            {formError && <FormError role="alert">{formError}</FormError>}
          </WorkflowForm>

          <IntroActions>
            <TextButton type="button" onClick={showSampleReport}>Preview a sample report</TextButton>
            <PrimaryButton type="button" onClick={startAssessment}>
              Start Quick Score <ArrowRightIcon aria-hidden="true" />
            </PrimaryButton>
          </IntroActions>
        </Intro>
      </Shell>
    );
  }

  return (
    <Shell>
      <AssessmentLayout>
        <SectionRail aria-label="Assessment sections">
          <RailTitle>Quick Score</RailTitle>
          <RailList>
            {assessmentSections.map((section) => {
              const isActive = section.number === question.section;
              const isComplete = section.number < question.section;
              return (
                <RailItem key={section.number} $active={isActive} $complete={isComplete} aria-current={isActive ? 'step' : undefined}>
                  <span>{isComplete ? <CheckIcon aria-hidden="true" /> : section.number}</span>
                  <span>{section.label}</span>
                </RailItem>
              );
            })}
          </RailList>
        </SectionRail>

        <QuestionPanel>
          <ProgressMeta>
            <span>Question {currentQuestion + 1} of {quickScoreQuestions.length}</span>
            <span>{progress}%</span>
          </ProgressMeta>
          <ProgressTrack
            $progress={progress}
            role="progressbar"
            aria-label="Assessment progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
          />

          <QuestionSection>Section {question.section} · {currentSection?.label}</QuestionSection>
          <QuestionTitle ref={questionHeadingRef} tabIndex={-1}>{question.prompt}</QuestionTitle>
          <QuestionGuidance>{question.guidance}</QuestionGuidance>

          <Options>
            <legend>{question.prompt}</legend>
            {question.options.map((option) => {
              const selected = answers[question.id] === option.value;
              return (
                <OptionLabel key={option.value} $selected={selected}>
                  <input
                    type="radio"
                    name={question.id}
                    value={option.value}
                    checked={selected}
                    onChange={() => setAnswers((current) => ({ ...current, [question.id]: option.value }))}
                  />
                  <OptionMark $selected={selected}><CheckIcon aria-hidden="true" /></OptionMark>
                  <OptionCopy><strong>{option.label}</strong><small>{option.description}</small></OptionCopy>
                </OptionLabel>
              );
            })}
          </Options>

          <Navigation>
            <BackButton type="button" onClick={goBack}>
              <ArrowLeftIcon aria-hidden="true" /> Back
            </BackButton>
            <PrimaryButton type="button" onClick={advance} disabled={!answers[question.id]}>
              {currentQuestion === quickScoreQuestions.length - 1 ? 'View my report' : 'Continue'}
              <ArrowRightIcon aria-hidden="true" />
            </PrimaryButton>
          </Navigation>
        </QuestionPanel>
      </AssessmentLayout>
    </Shell>
  );
};

export default AIReadinessAssessment;
